import { ReactNode, useRef, type RefObject } from 'react'
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet'
import './HoldingsSheet.css'

type HoldingsSheetProps = {
  isOpen: boolean
  onClose: () => void
  header: ReactNode
  footer: ReactNode
  children: ReactNode
  /** Visible heading id for aria-labelledby (HoldingViewall_B08). */
  titleId?: string
  initialFocusRef?: RefObject<HTMLElement>
}

export default function HoldingsSheet({
  isOpen,
  onClose,
  header,
  footer,
  children,
  titleId,
  initialFocusRef
}: HoldingsSheetProps) {
  const sheetRef = useRef<BottomSheetRef>(null)
  return (
    <BottomSheet
      open={isOpen}
      onDismiss={onClose}
      ref={sheetRef}
      header={header}
      footer={footer}
      initialFocusRef={initialFocusRef ?? false}
      snapPoints={({ minHeight }) => [minHeight]}
      maxHeight={window?.innerHeight * 0.9}
      defaultSnap={({ minHeight }) => minHeight}
      className='holdings-sheet'
      onSpringStart={(event) => {
        if (event.type !== 'OPEN') return
        // The library's overlay is its dialog element, with a hardcoded tabIndex=-1 and no name; TalkBack makes
        // that focusable container an extra stop that reads the whole sheet (ViewAll Holdings_B09), so name it
        // here and drop the tabindex. Focus-trap still lands on initialFocusRef.
        const overlay = document.querySelector<HTMLElement>('.holdings-sheet [data-rsbs-overlay]')
        if (!overlay) return
        overlay.removeAttribute('tabindex')
        if (titleId) {
          overlay.setAttribute('aria-labelledby', titleId)
        } else {
          overlay.setAttribute('aria-label', 'All holdings')
        }
      }}
    >
      {children}
    </BottomSheet>
  )
}
